import {
  BackupDto,
  BackupId,
  BackupLogDto,
  BackupLogRepositoryInterface,
  BackupRepoDto,
  BackupRepoId,
  BackupRepoRepositoryInterface,
  BackupRepoSecretsDto,
  BackupRepositoryInterface,
  BackupScheduleDto,
  BackupScheduleId,
  BackupScheduleRepositoryInterface,
  BackupServerDto,
  BackupServerId,
  BackupServerRepositoryInterface,
  BackupSummaryDto,
  BackupTaskDto,
  BackupTaskId,
  BackupTaskRepositoryInterface,
  BackupTaskSummaryDto,
  ConfigResetActionId,
  ConfigResetActionLogDto,
  ConfigResetDto,
  ConfigResetId,
  ConfigResetListDto,
  ConfigResetRepositoryInterface,
  ConfigResetSummaryDto,
  CreateBackupRepoDTO,
  CreateTaskScheduleDto,
  CreateBackupServerDto,
  CreateForgetScheduleDto,
  CreateInstantTaskDto,
  CreateInstantForgetRepositoryDto,
  CreateInstantPruneRepositoryDto,
  CreateScheduledForgetRepositoryDto,
  CreateSchedulePruneRepositoryDto,
  DatabaseBackupConfigDto,
  DatabaseBackupId,
  DatabaseBackupSummaryDto,
  FailedConfigCollectionLogDto,
  ForgetServerRepositoryInterface,
  LockReason,
  LockType,
  PagingOptions,
  PagingWithSearchOptions,
  QueryResultDto,
  RepoLocksDto,
  RepoLocksRepositoryInterface,
  RestoreBackupDto,
  RestoreDatabase,
  RestoreServer,
  RestoreTaskId,
  RestoreTaskRepositoryInterface,
  RestoreTaskSummaryDto,
  RestoreWebsite, TaskScheduleDto,
  TaskScheduleRepositoryInterface,
  WebsiteBackupSummaryDto,
  WebsiteGroupRestoreBackupDto, TaskRepositoryInterface, TaskDto,
} from "@/services/Backup/interfaces";

import { AxiosInstance } from 'axios';

export class BackupServerRepository implements BackupServerRepositoryInterface {
  constructor(private readonly axios: AxiosInstance) {
  }

  public baseURL: string = 'backups/servers';

  public async searchBackupServers(query: PagingWithSearchOptions): Promise<QueryResultDto<BackupServerDto>> {
    const response = await this.axios.get(this.baseURL, { params: query });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public createBackupServer(server: CreateBackupServerDto): Promise<BackupServerDto> {
    return this.axios.post(this.baseURL, server);
  }

  public async getBackupServer(id: BackupServerId): Promise<BackupServerDto | null> {
    const response = await this.axios.get(`${this.baseURL}/${id}`);

    return response.data;
  }

  public updateBackupServer(server: BackupServerDto): Promise<void> {
    return this.axios.patch(`${this.baseURL}/${server.id}`, server);
  }

  public async deleteBackupServer(id: BackupServerId): Promise<void> {
    await this.axios.delete(`${this.baseURL}/${id}`);
  }

  public async pruneBackupRepoInstant({ backupServerId, ...data }: CreateInstantPruneRepositoryDto): Promise<void> {
    await this.axios.post(`${this.baseURL}/${backupServerId}/prune`, data);
  }

  public async pruneBackupRepoScheduled({ backupServerId, ...data }: CreateSchedulePruneRepositoryDto): Promise<void> {
    await this.axios.post(`${this.baseURL}/${backupServerId}/schedule-prune`, data);
  }

  public async forgetBackupRepoInstant({ backupServerId, ...data }: CreateInstantForgetRepositoryDto): Promise<void> {
    await this.axios.post(`${this.baseURL}/${backupServerId}/forget`, data);
  }

  public async forgetBackupRepoScheduled({
                                           backupServerId,
                                           ...data
                                         }: CreateScheduledForgetRepositoryDto): Promise<void> {
    await this.axios.post(`${this.baseURL}/${backupServerId}/schedule-forget`, data);
  }
}

export class BackupRepository implements BackupRepositoryInterface {
  public baseURL = "backups/backups";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async restoreBackup(data: RestoreBackupDto): Promise<void> {
    await this.axios.post('backups/tasks/restores', {
      backupId: data.targetBackupId,
      websiteRestoreList: [
        {
          websiteBackupId: data.websiteBackupId,
          restoreConfigTypes: data.options?.restoreConfigTypes,
          restoreBackupTargets: data.options?.restoreBackupTargets,
        },
      ],
    }, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public async restoreWebsiteGroupBackup(data: WebsiteGroupRestoreBackupDto): Promise<void> {
    await this.axios.post('backups/tasks/restores/websiteGroup', {
      backupId: data.targetBackupId,
      websiteGroupId: data.websiteGroupId,
    }, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public async searchBackups(serverId: string | undefined, query: PagingOptions): Promise<QueryResultDto<BackupSummaryDto>> {
    const response = await this.axios.get(this.baseURL, { params: { ...query, targetOriginServerId: serverId } });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }


  public async createTerminatedBackup(websiteGroupId: number): Promise<void> {
    return this.axios.post(`backups/tasks/terminated-backups`, {
      websiteGroupId: websiteGroupId,
    });
  }

  public async getBackup(id: BackupId): Promise<BackupDto | null> {
    const response = await this.axios.get(`${this.baseURL}/${id}`);

    return response.data;
  }

  public async deleteBackup(id: BackupId): Promise<void> {
    await this.axios.delete(`${this.baseURL}/${id}`);
  }

  public async getWebsiteBackups(
    id: BackupId,
    websiteId: number | undefined,
    websiteDomain: string | undefined,
    websiteGroupId: number | undefined,
    options: PagingOptions | undefined,
  ): Promise<QueryResultDto<WebsiteBackupSummaryDto>> {
    const response = await this.axios.get(`${this.baseURL}/websites/search`, {
      params: {
        ...options,
        domain: websiteDomain,
        backupId: id,
        websiteId,
        websiteGroupId,
      },
    });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async getWebsiteBackup(backupId: number, websiteId: number): Promise<WebsiteBackupSummaryDto> {
    const response = await this.axios.get(`${this.baseURL}/${backupId}/websites/${websiteId}`);

    return response.data;
  }

  public async getDatabaseBackups(id: BackupId, options: PagingOptions): Promise<QueryResultDto<DatabaseBackupSummaryDto>> {
    const response = await this.axios.get(`${this.baseURL}/${id}/database`, { params: options });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async getDatabaseBackupConfig(id: DatabaseBackupId): Promise<DatabaseBackupConfigDto> {
    const response = await this.axios.get(`${this.baseURL}/database-backups/${id}/config`);

    return response.data;
  }
}

export class RestoreTaskRepository implements RestoreTaskRepositoryInterface {
  public baseURL = "backups/tasks/restores";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async searchRestoreTasks(serverId: string | undefined, websiteId: number | undefined, query: PagingOptions): Promise<QueryResultDto<RestoreTaskSummaryDto>> {
    const response = await this.axios.get(this.baseURL, {
      params: {
        ...query,
        targetOriginServerId: serverId,
        websiteId,
      },
    });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async getRestoreTask(id: RestoreTaskId): Promise<RestoreTaskSummaryDto> {
    const response = await this.axios.get(`${this.baseURL}/${id}`);

    return response.data;
  }

  public async getWebsiteRestoreData(id: RestoreTaskId): Promise<RestoreWebsite> {
    const response = await this.axios.get(`${this.baseURL}/${id}/websites`);

    return response.data;
  }

  public async getDatabaseRestoreData(id: RestoreTaskId): Promise<RestoreDatabase> {
    const response = await this.axios.get(`${this.baseURL}/${id}/databases`);

    return response.data;
  }

  public async getServerRestoreData(id: RestoreTaskId): Promise<RestoreServer> {
    const response = await this.axios.get(`${this.baseURL}/${id}/servers`);

    return response.data;
  }
}

export class BackupRepoRepository implements BackupRepoRepositoryInterface {
  public baseURL = "/backups/repositories" as const;

  constructor(private readonly axios: AxiosInstance) {
  }

  public async createBackupRepo(data: CreateBackupRepoDTO): Promise<void> {
    await this.axios.post(`${this.baseURL}`, data);
  }

  public async getAllBackupRepos(query: PagingWithSearchOptions): Promise<QueryResultDto<BackupRepoDto> | null> {
    const response = await this.axios.get(this.baseURL, { params: query });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async getBackupRepo(id: BackupRepoId): Promise<BackupRepoDto | null> {
    const response = await this.axios.get(`${this.baseURL}/${id}`);

    return response.data;
  }

  public async getBackupRepoSecrets(id: BackupRepoId): Promise<BackupRepoSecretsDto | null> {
    const response = await this.axios.get(`${this.baseURL}/${id}/secrets`);

    return {
      ...response.data,
      targetOriginServerName: response.data.targetOriginServer?.originServerHostname,
    };
  }

  public async deleteBackupRepo(id: BackupRepoId): Promise<void> {
    await this.axios.delete(`${this.baseURL}/${id}`);
  }


}

export class BackupScheduleRepository implements BackupScheduleRepositoryInterface {
  public baseURL = "backups/schedules";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async searchBackupSchedules(query: PagingOptions): Promise<QueryResultDto<BackupScheduleDto>> {
    const response = await this.axios.get(this.baseURL, { params: query });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async createBackupSchedule(backupSchedule: CreateTaskScheduleDto): Promise<void> {
    return this.axios.post(this.baseURL, backupSchedule);
  }

  public async getBackupSchedule(id: BackupScheduleId): Promise<BackupScheduleDto | null> {
    const response = await this.axios.get(`${this.baseURL}/${id}`);

    return response.data;
  }

  public updateBackupSchedule(backupSchedule: BackupScheduleDto): Promise<void> {
    return this.axios.patch(`${this.baseURL}/${backupSchedule.id}`, backupSchedule);
  }

  public async deleteBackupSchedule(id: BackupScheduleId): Promise<void> {
    await this.axios.delete(`${this.baseURL}/${id}`);
  }
}

export class BackupTaskRepository implements BackupTaskRepositoryInterface {
  public baseURL = "backups/tasks";

  constructor(private readonly axios: AxiosInstance) {
  }

  public getBackupTask(id: BackupTaskId): Promise<BackupTaskDto | null> {
    throw new Error("Method not implemented.");
  }

  public async searchBackupTasks(query: PagingOptions): Promise<QueryResultDto<BackupTaskSummaryDto>> {
    const response = await this.axios.get(this.baseURL, { params: query });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }
}

export class BackupLogRepository implements BackupLogRepositoryInterface {
  public baseURL = "backups/logs";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async searchBackupLogs(query: PagingOptions): Promise<QueryResultDto<BackupLogDto>> {
    const response = await this.axios.get(`${this.baseURL}/backups`, { params: query });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async searchFailedConfigCollectionLogs(query: PagingOptions): Promise<QueryResultDto<FailedConfigCollectionLogDto>> {
    const response = await this.axios.get(`${this.baseURL}/config-collection`, { params: query });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }
}

export class ConfigResetRepository implements ConfigResetRepositoryInterface {
  public baseURL = "config-orchestrator";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async deleteConfigResetActionLog(id: number): Promise<void> {
    await this.axios.delete(`${this.baseURL}/logs/${id}`);
  }

  public async searchConfigResetActionLogs(query: PagingOptions): Promise<QueryResultDto<ConfigResetActionLogDto>> {
    const response = await this.axios.get(`${this.baseURL}/logs`, { params: query });

    const [data, total] = response.data;
    return {
      data,
      total,
    };
  }

  public async getConfigReset(id: ConfigResetId): Promise<ConfigResetDto | null> {
    const response = await this.axios.get(`${this.baseURL}/config-restore/configs/${id}`);

    return response.data;
  }

  public async getConfigResetByTask(id: RestoreTaskId): Promise<ConfigResetListDto> {
    const response = await this.axios.get(`${this.baseURL}/config-restore/configs/task/${id}`);

    return response.data;
  }

  public async searchConfigResets(query: PagingOptions): Promise<QueryResultDto<ConfigResetSummaryDto>> {
    const response = await this.axios.get(`${this.baseURL}/config-restore/configs`, { params: query });
    const [data, total] = response.data;
    return {
      data,
      total,
    };
  }

  public async deleteConfigReset(id: ConfigResetId): Promise<void> {
    await this.axios.delete(`${this.baseURL}/config-restore/configs/${id}`);
  }

  public deleteConfigResetAction(id: ConfigResetActionId): Promise<void> {
    throw new Error("Method not implemented.");
  }
}


export class ForgetServerRepository implements ForgetServerRepositoryInterface {
  public baseURL = "backups/servers/schedules/forget";

  constructor(private readonly axios: AxiosInstance) {
  }

  createScheduleForgetServer(forgetSchedule: CreateForgetScheduleDto): Promise<void> {
    return this.axios.post(this.baseURL, forgetSchedule);
  }

}

export class RepoLocksRepository implements RepoLocksRepositoryInterface {
  constructor(private readonly axios: AxiosInstance) {
  }

  public baseURL = "/backups/repo-locks";

  public async searchReposLocks(request: PagingOptions): Promise<QueryResultDto<RepoLocksDto>> {

    const response = await this.axios.get(`${this.baseURL}`, { params: request });
    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public async getRepoLockById(repoLockId: number): Promise<RepoLocksDto> {
    const response = await this.axios.get(`${this.baseURL}/locks/${repoLockId}`);

    return response.data;
  }

  public async toggleLockState(
    repoId: number,
    newState: boolean,
    type: LockType = LockType.LOCK_TYPE_LOCK_ALL,
  ): Promise<void> {
    if (newState) {
      return this.axios.post(`${this.baseURL}/${repoId}/lock`, { reason: LockReason.LOCK_REASON_MANUAL, type });
    }

    return this.axios.post(`${this.baseURL}/${repoId}/unlock`);
  }

  public async deleteRepoLock(repoLockId: number): Promise<void> {
    await this.axios.delete(`${this.baseURL}/${repoLockId}`);
  }

}

export class TaskScheduleRepository implements TaskScheduleRepositoryInterface {
  public baseURL = "backups/task-manager/schedules";

  constructor(private readonly axios: AxiosInstance) {
  }

  public async searchTaskSchedules(type?: string, query?: PagingOptions): Promise<QueryResultDto<TaskScheduleDto>> {
    const response = await this.axios.get(this.baseURL, {
      params: {
        type,
        page: query?.page,
        pageSize: query?.pageSize,
      },
    });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  public createBackupSchedule(backupSchedule: CreateTaskScheduleDto): Promise<void> {
    return this.axios.post(this.baseURL, backupSchedule);
  }
}

export class TaskRepository implements TaskRepositoryInterface {
  public baseURL = "backups/task-manager/tasks";

  public async searchTasks(type?: string, targetServer?: string, query?: PagingOptions): Promise<QueryResultDto<TaskDto>> {
    const response = await this.axios.get(this.baseURL, {
      params: {
        type,
        targetServer,
        page: query?.page,
        pageSize: query?.pageSize,
      },
    });

    const [data, total] = response.data;

    return {
      data,
      total,
    };
  }

  constructor(private readonly axios: AxiosInstance) {
  }

  public createInstantTask(createDto: CreateInstantTaskDto): Promise<void> {
    return this.axios.post(this.baseURL, createDto);
  }

  public failTask(type:string, id:number): Promise<void> {
    return this.axios.patch(this.baseURL, {
      type,
      taskId: id
    });
  }
}

